import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import { withLocale } from '@dietlabs/components';
import { reportError } from '@dietlabs/utils';

import { formatIsoDate } from 'utils/dateFormatter';
import CachedDataMessage from '../../../CachedDataMessage/CachedDataMessage';
import DayPlan from '../../components/DayPlan';
import DayPlanSummary from '../../components/DayPlanSummary';
import Meal from '../../components/DayPlanMeal';
import Training from '../../components/DayPlanTraining';
import Dish from '../../components/DayPlanDish';
import IngredientList from '../../components/DayPlanIngredientList';
import Ingredient from '../../components/DayPlanIngredient';
import IngredientReplacementList from '../../components/DayPlanIngredientReplacementList';
import IngredientReplacement from '../../components/DayPlanIngredientReplacement';
import PrintForm from '../../components/DayPlanPrintForm';

import MealReplacementList from '../../components/DayPlanMealReplacementList';
import MealReplacement from '../../components/DayPlanMealReplacement';

import DayPlanChangeDay from '../../components/DayPlanChangeDay';
import DayReplacement from '../../components/DayPlanDayReplacement';

import MealNavigation from '../../components/DayPlanMealNavigation';
import MealLink from '../../components/DayPlanMealLink';

class DayPlanContainer extends Component {
    static propTypes = {
        renderTimeline: PropTypes.func.isRequired,
        dayPlan: PropTypes.shape({
            canBeCopiedToDate: PropTypes.bool.isRequired,
            date: PropTypes.instanceOf(Date).isRequired,
            diet: PropTypes.shape({
                id: PropTypes.number.isRequired,
                kcal: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                macro: PropTypes.shape({
                    animalProtein: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                    carbohydrates: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                    fat: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                    protein: PropTypes.shape({
                        grams: PropTypes.number.isRequired,
                        kcal: PropTypes.number.isRequired,
                        percentage: PropTypes.number.isRequired,
                    }).isRequired,
                }).isRequired,
                phase: PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    identifier: PropTypes.string.isRequired,
                }).isRequired,
            }),
            events: PropTypes.arrayOf(PropTypes.shape()),
            showMacros: PropTypes.bool.isRequired,
        }).isRequired,
        availableDays: PropTypes.arrayOf(PropTypes.object).isRequired,
        categories: PropTypes.arrayOf(PropTypes.shape()).isRequired,
        goal: PropTypes.shape({
            reachedBecauseOfLoseWeight: PropTypes.bool.isRequired,
            reachedBecauseOfPutOnWeight: PropTypes.bool.isRequired,
            lostBecauseOfLoseWeight: PropTypes.bool.isRequired,
            lostBecauseOfPutOnWeight: PropTypes.bool.isRequired,
        }).isRequired,
        isHolidayMenu: PropTypes.bool.isRequired,
        isTimeToNagForCurrentMeasurement: PropTypes.bool.isRequired,
        cacheHit: PropTypes.bool.isRequired,
        hasNetworkError: PropTypes.bool.isRequired,
        copyDayPlan: PropTypes.func.isRequired,
        alreadyCopied: PropTypes.bool.isRequired,
        print: PropTypes.bool,
        replaceProduct: PropTypes.func.isRequired,
        loadDayPlanMealReplacements: PropTypes.func.isRequired,
        replaceMeal: PropTypes.func.isRequired,
        loadDayPlanDayReplacements: PropTypes.func.isRequired,
        replaceDay: PropTypes.func.isRequired,
        prefetchSurroundingDays: PropTypes.func,
        loadDayPlanForTomorrow: PropTypes.func.isRequired,
        fastingStatistics: PropTypes.shape({
            dietsCount: PropTypes.number,
            currentDietLength: PropTypes.number,
            currentDietProgress: PropTypes.number,
        }).isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
            search: PropTypes.string.isRequired,
        }).isRequired,
    };

    static defaultProps = {
        print: false,
        prefetchSurroundingDays: undefined,
    };

    openMealRefArray = [];

    state = {
        dayPlanForTomorrow: undefined,
    };

    componentDidMount = async () => {
        const dayPlanForTomorrow = await this.props.loadDayPlanForTomorrow();
        this.setState({ dayPlanForTomorrow });

        if (!this.props.print) {
            this.props.prefetchSurroundingDays();
        }

        const urlParams = new URLSearchParams(this.props.location.search);
        if (urlParams.has('showReplacements')) {
            this.triggerLoadDayReplacements();
        }
    };

    componentDidUpdate = async prevProps => {
        const dayPlanForTomorrow = await this.props.loadDayPlanForTomorrow();
        if (
            JSON.stringify(this.state.dayPlanForTomorrow) !==
            JSON.stringify(dayPlanForTomorrow)
        ) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ dayPlanForTomorrow });
        }

        if (!this.props.print) {
            this.props.prefetchSurroundingDays();
        }

        if (
            formatIsoDate(prevProps.dayPlan.date) !==
            formatIsoDate(this.props.dayPlan.date)
        ) {
            const urlParams = new URLSearchParams(this.props.location.search);
            if (urlParams.has('showReplacements')) {
                this.triggerLoadDayReplacements();
            }
        }
    };

    triggerLoadDayReplacements() {
        this.changeDayRef.handleClick();
    }

    triggerOpenMeal(mealId) {
        if (this.openMealRefArray[mealId]) {
            this.openMealRefArray[mealId].openMeal();
        }
    }

    renderIngredientReplacements = (
        replacements,
        productAmountId,
        productId,
        productKey
    ) =>
        replacements.map(replacement => (
            <IngredientReplacement
                key={replacement.productId}
                replacementId={replacement.productId}
                name={replacement.name}
                measurements={replacement.measurements}
                replaceProduct={this.props.replaceProduct}
                productId={productId}
                productKey={productKey}
                productAmountId={productAmountId}
                replaceProductSuccess={() => ''}
            />
        ));

    renderIngredientReplacementsList = (
        replacements,
        originalProductId,
        productId,
        productKey,
        original,
        productAmountId
    ) => (
        <IngredientReplacementList
            shouldRenderOryginalProduct={originalProductId !== productId}
            renderOriginalProduct={() =>
                this.renderIngredientReplacements(
                    [original],
                    productAmountId,
                    productId,
                    productKey
                )
            }
            hasReplacements={replacements.length > 0}
        >
            {() =>
                this.renderIngredientReplacements(
                    replacements,
                    productAmountId,
                    productId,
                    productKey
                )
            }
        </IngredientReplacementList>
    );

    renderIngredients = ingredients =>
        ingredients.map(ingredient => (
            <Ingredient
                key={ingredient.productId}
                name={ingredient.name}
                measurements={ingredient.measurements}
                saleProductVariant={ingredient.saleProductVariant}
                hasReplacement={ingredient.hasReplacement}
                originalProductId={ingredient.original.productId}
                productId={ingredient.productId}
                productKey={ingredient.key}
                print={this.props.print}
            >
                {() =>
                    this.renderIngredientReplacementsList(
                        ingredient.replacements,
                        ingredient.original.productId,
                        ingredient.productId,
                        ingredient.key,
                        ingredient.original,
                        ingredient.productAmountId
                    )
                }
            </Ingredient>
        ));

    renderIngredientList = ingredients => {
        const filteredIngredients = ingredients.filter(
            ingredient =>
                !ingredient.category.isSeasoning &&
                !ingredient.category.isOptional
        );

        const onlySeasoningsIngredients = ingredients.filter(
            ingredient => ingredient.category.isSeasoning
        );

        const optionalIngredients = ingredients.filter(
            ingredient => ingredient.category.isOptional
        );

        return (
            <IngredientList
                renderIngredients={() =>
                    this.renderIngredients(filteredIngredients)
                }
                renderOnlySeasoningsIngredients={() =>
                    this.renderIngredients(onlySeasoningsIngredients)
                }
                renderOptionalIngredients={() =>
                    this.renderIngredients(optionalIngredients)
                }
            />
        );
    };

    renderDishes = (dishes, name, preparationVideoUrl) =>
        dishes.map(dish => (
            <Dish
                key={dish.key}
                name={dish.name}
                mealName={name}
                recipe={dish.recipe}
                recipeNote={dish.recipeNote}
                triangleOfPower={dish.triangleOfPower}
                isFirstOccurance={dish.isFirstOccurance}
                isLastOccurance={dish.isLastOccurance}
                isPortioned={dish.isPortioned}
                portions={dish.portions}
                portionsTotal={dish.portionsTotal}
                data-test="dish-component"
                print={this.props.print}
                preparationVideoUrl={preparationVideoUrl}
            >
                {() => this.renderIngredientList(dish.ingredients)}
            </Dish>
        ));

    renderMealReplacements = (
        originalMealId,
        mealKey,
        replacements,
        clickAction
    ) =>
        replacements.map(mealReplacement => (
            <MealReplacement
                key={mealReplacement.id}
                originalMealId={originalMealId}
                mealKey={mealKey}
                mealReplacement={mealReplacement}
                replaceMeal={this.props.replaceMeal}
                closeAndScroll={clickAction.closeAndScroll}
                replaceMealSuccess={clickAction.replaceMealSuccess}
            />
        ));

    renderMealReplacementsList = (
        replacements,
        originalMealId,
        mealId,
        mealKey,
        original,
        clickAction
    ) => (
        <MealReplacementList
            originalMealId={originalMealId}
            mealId={mealId}
            renderOriginalMeal={() =>
                this.renderMealReplacements(
                    originalMealId,
                    mealKey,
                    [original],
                    clickAction
                )
            }
            replacements={replacements}
        >
            {() =>
                this.renderMealReplacements(
                    originalMealId,
                    mealKey,
                    replacements,
                    clickAction
                )
            }
        </MealReplacementList>
    );

    renderEvents = events =>
        events.map(event => {
            if (event.__typename === 'Meal') {
                return (
                    <Meal
                        key={event.key}
                        mealId={event.id}
                        dietPhase={this.props.dayPlan.diet.phase.identifier.toLowerCase()}
                        preparationTime={event.preparationTime}
                        name={event.name}
                        kcal={event.kcal}
                        macro={event.macro}
                        showMacros={this.props.dayPlan.showMacros}
                        originalId={event.original.id}
                        data-test="meal-component"
                        loadDayPlanMealReplacements={
                            this.props.loadDayPlanMealReplacements
                        }
                        renderMealReplacementsList={(
                            replacements,
                            originalMealId,
                            { closeAndScroll, replaceMealSuccess }
                        ) =>
                            this.renderMealReplacementsList(
                                replacements,
                                originalMealId,
                                event.id,
                                event.key,
                                event.original,
                                { closeAndScroll, replaceMealSuccess }
                            )
                        }
                        print={this.props.print}
                        onRef2={(ref, id) => {
                            this.openMealRefArray[id] = ref;
                        }}
                    >
                        {() =>
                            this.renderDishes(
                                event.dishes,
                                event.name,
                                event.preparationVideoUrl
                            )
                        }
                    </Meal>
                );
            }
            if (event.__typename === 'Training') {
                return (
                    <Training
                        key={event.__typename + event.id}
                        activities={event.activities}
                        burnedCalories={event.burnedCalories}
                        duration={event.duration}
                        data-test="training-component"
                    />
                );
            }
            reportError(
                new Error(`Unknown event typename: ${event.__typename}`)
            );
            return '';
        });

    renderMealLinks = () => (
        <MealNavigation>
            {listRef =>
                this.props.dayPlan.events.map(event => {
                    if (event.__typename === 'Meal') {
                        return (
                            <MealLink
                                key={event.key}
                                name={event.name}
                                mealId={event.id}
                                listRef={listRef}
                                triggerOpenMeal={mealId =>
                                    this.triggerOpenMeal(mealId)
                                }
                            />
                        );
                    }
                    return '';
                })
            }
        </MealNavigation>
    );

    renderSummary = (canBeCopiedToDate, alreadyCopied) => (
        <DayPlanSummary
            protein={this.props.dayPlan.diet.macro.protein.percentage}
            fat={this.props.dayPlan.diet.macro.fat.percentage}
            carbohydrates={
                this.props.dayPlan.diet.macro.carbohydrates.percentage
            }
            canBeCopiedToDate={canBeCopiedToDate}
            copyDayPlan={this.props.copyDayPlan}
            alreadyCopied={alreadyCopied}
            print={this.props.print}
            data-test="summary-component"
        >
            {() => (
                <React.Fragment>
                    {this.renderChangeDay()}
                    {this.renderPrintForm()}
                </React.Fragment>
            )}
        </DayPlanSummary>
    );

    renderDayReplacements = (replacements, search, changeDaySuccess) =>
        replacements.map(replacement => (
            <DayReplacement
                key={replacement.dietSetId}
                dayReplacement={replacement}
                search={search}
                replaceDay={this.props.replaceDay}
                changeDaySuccess={changeDaySuccess.changeDaySuccess}
            />
        ));

    renderChangeDay = () => (
        <DayPlanChangeDay
            onRef1={ref => {
                this.changeDayRef = ref;
            }}
            loadDayPlanDayReplacements={this.props.loadDayPlanDayReplacements}
            renderOriginalDay={(original, search, { changeDaySuccess }) =>
                this.renderDayReplacements([original], search, {
                    changeDaySuccess,
                })
            }
            date={this.props.dayPlan.date}
        >
            {(replacements, search, { changeDaySuccess }) =>
                this.renderDayReplacements(replacements, search, {
                    changeDaySuccess,
                })
            }
        </DayPlanChangeDay>
    );

    renderPrintForm = () => (
        <PrintForm
            date={this.props.dayPlan.date}
            availableDays={this.props.availableDays}
        />
    );

    render() {
        const {
            dayPlan,
            availableDays,
            categories,
            goal,
            isHolidayMenu,
            isTimeToNagForCurrentMeasurement,
            cacheHit,
            hasNetworkError,
            alreadyCopied,
            print,
            fastingStatistics,
        } = this.props;

        return (
            <DayPlan
                kcal={dayPlan.diet.kcal}
                date={dayPlan.date}
                name={dayPlan.diet.name}
                phase={dayPlan.diet.phase}
                goal={goal}
                isHolidayMenu={isHolidayMenu}
                isTimeToNagForCurrentMeasurement={
                    isTimeToNagForCurrentMeasurement
                }
                availableDays={availableDays}
                categories={categories}
                renderTimeline={position => this.props.renderTimeline(position)}
                print={print}
                triggerLoadDayReplacements={() =>
                    this.triggerLoadDayReplacements()
                }
                dayPlanForTomorrow={this.state.dayPlanForTomorrow}
                showMacros={this.props.dayPlan.showMacros}
                renderMealLinks={() => this.renderMealLinks()}
                fastingStatistics={fastingStatistics}
            >
                {() => (
                    <React.Fragment>
                        <CachedDataMessage {...{ cacheHit, hasNetworkError }} />
                        {this.renderEvents(dayPlan.events)}
                        {this.renderSummary(
                            dayPlan.canBeCopiedToDate,
                            alreadyCopied
                        )}
                    </React.Fragment>
                )}
            </DayPlan>
        );
    }
}

export { DayPlanContainer };
export default withRouter(withLocale(DayPlanContainer));
